// src/rms/ads/useViewability.js
// ============================================================================
// MRC/IAB viewability measurement
// ============================================================================
// A display impression only counts as viewable when at least 50% of its pixels
// are in the viewport for at least 1 continuous second. Almost all programmatic
// demand buys on that basis (vCPM), and direct advertisers ask for it.
//
// Two separate signals come out of this hook:
//
//   inView    the slot is near/inside the viewport → SAFE TO RENDER the creative.
//             Rendering earlier wastes an ad request on something nobody sees.
//   viewable  the MRC threshold has been met → SAFE TO COUNT the impression.
//
// Counting on render, as we did before, overstates delivery — which is fine
// until the first time you show those numbers to an advertiser.
// ============================================================================

import { useEffect, useRef, useState } from "react";

const VIEWABLE_RATIO = 0.5;      // 50% of pixels
const VIEWABLE_MS = 1000;        // for 1 continuous second
const PRELOAD_MARGIN = "200px";  // start rendering just before it scrolls in

// Safety net. If the observer has told us nothing by now — a backgrounded tab,
// a non-compositing embedded view, an exotic browser — render anyway.
//
// Lazy rendering must degrade to "render regardless", never to "render never":
// a slot that stays blank earns nothing at all, which is far worse than one
// that renders slightly early. The impression is still gated on real
// viewability, so this cannot inflate reported delivery.
const RENDER_FALLBACK_MS = 1500;

export default function useViewability({ enabled = true, onViewable } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const [viewable, setViewable] = useState(false);

  const timerRef = useRef(null);
  const firedRef = useRef(false);
  const callbackRef = useRef(onViewable);
  callbackRef.current = onViewable;

  useEffect(() => {
    if (!enabled) return undefined;
    const node = ref.current;
    if (!node) return undefined;

    // Environments without IntersectionObserver (old browsers, jsdom in tests)
    // fall back to treating the slot as visible rather than never rendering.
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      setViewable(true);
      if (!firedRef.current) {
        firedRef.current = true;
        callbackRef.current?.();
      }
      return undefined;
    }

    // Two observers: a generous one to decide when to render, and a strict one
    // at the MRC threshold to decide when it counts.
    const renderObserver = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin: PRELOAD_MARGIN, threshold: 0 }
    );

    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const viewObserver = new IntersectionObserver(
      ([entry]) => {
        const meets = entry.isIntersecting && entry.intersectionRatio >= VIEWABLE_RATIO;

        if (!meets) {
          // Interrupted before the full second — the clock restarts, it does
          // not accumulate. That is what "continuous" means in the standard.
          clearTimer();
          return;
        }
        if (firedRef.current || timerRef.current) return;

        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          firedRef.current = true;
          setViewable(true);
          callbackRef.current?.();
        }, VIEWABLE_MS);
      },
      { threshold: [0, VIEWABLE_RATIO, 1] }
    );

    renderObserver.observe(node);
    viewObserver.observe(node);

    // See RENDER_FALLBACK_MS: never leave a slot permanently blank.
    const fallback = setTimeout(() => setInView(true), RENDER_FALLBACK_MS);

    // A backgrounded tab is not viewable however much of it is on screen.
    const onVisibility = () => {
      if (document.hidden) clearTimer();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      renderObserver.disconnect();
      viewObserver.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      clearTimeout(fallback);
      clearTimer();
    };
  }, [enabled]);

  /** Call when the creative changes — the new one must earn its own view. */
  const reset = () => {
    firedRef.current = false;
    setViewable(false);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  return { ref, inView, viewable, reset };
}

export { VIEWABLE_RATIO, VIEWABLE_MS };
